import { useEffect, useState } from 'react';

/**
 * Which section is currently being read, for the capstone TOC.
 *
 * IntersectionObserver rather than a scroll listener, and read-only: the hook
 * never scrolls anything, so it cannot fight the user's own scrolling or
 * fight the browser's native `#fragment` jump. The top margin matches the
 * sticky header offset the stylesheet already uses (`--toc-offset`), and the
 * bottom margin keeps the last visible heading — not whatever is grazing the
 * bottom edge — as the answer.
 */
export function useScrollSpy(
  ids: readonly string[],
  rootMargin = '-88px 0px -55% 0px',
): string | null {
  const [activeId, setActiveId] = useState<string | null>(ids[0] ?? null);
  // A stable dependency: the ids array is rebuilt on every render upstream.
  const key = ids.join('|');

  useEffect(() => {
    const list = key ? key.split('|') : [];
    if (typeof IntersectionObserver === 'undefined' || list.length === 0) return;

    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id;
          if (entry.isIntersecting) visible.add(id);
          else visible.delete(id);
        }
        // Document order wins, so scrolling up through two visible sections
        // does not flicker between them.
        const first = list.find((id) => visible.has(id));
        if (first) setActiveId(first);
      },
      { rootMargin, threshold: 0 },
    );

    const observed: Element[] = [];
    for (const id of list) {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        observed.push(el);
      }
    }
    if (!observed.length) {
      observer.disconnect();
      return;
    }

    setActiveId((current) => (current && list.includes(current) ? current : (list[0] ?? null)));

    return () => observer.disconnect();
  }, [key, rootMargin]);

  return activeId;
}
